// axiosInstance.js
import axios from "axios";
import { handleUnauthorizedSession, isJwtAuthError } from "Utils/authSession";
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from "constants/localStorage.constants";

// Endpoints that require the Refresh-Token header in addition to the access token
const REFRESH_TOKEN_REQUIRED_ENDPOINTS = [
  '/publish_job_description',
  '/self_publish_jd',
  // Bulk upload queues one analysis task per row; the backend mints a fresh
  '/system-upload/bulk-upload',
  // Reviewer approval now also queues an async Talent Mobility cascade task
  // (same reason as the publish endpoints above) — it needs a refresh token
  // in case that task sits in queue long enough for the access token to expire.
  '/approval/review',
];

// Axios instance
export const Api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 9000000,
});

// Add a request interceptor to include auth token if available
Api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);

    config.headers = config.headers || {};

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set Content-Type to 'application/json' if not already set (e.g., by multipart/form-data)
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    const needsRefreshToken = REFRESH_TOKEN_REQUIRED_ENDPOINTS.some((endpoint) =>
      config.url?.endsWith(endpoint)
    );
    if (needsRefreshToken) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        config.headers['Refresh-Token'] = refreshToken;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor for global error handling
Api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    // flask-jwt-extended reports malformed/invalid access tokens as 422, not 401
    // (see isJwtAuthError) — those must force a logout the same as a 401 does.
    // 403 (forbidden/insufficient permissions) is intentionally left untouched.
    if (status === 401 || isJwtAuthError(error)) {
      console.warn("Unauthorized request. You may need to log in again.");
      handleUnauthorizedSession();
    }
    return Promise.reject(error);
  }
);


export const PublicApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_AUTH_URL || 'http://127.0.0.1:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 9000000,
});